import { Fragment, useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { compact, map, size } from "lodash"
import { IconButton, Popover, Divider } from "@mui/material"
import { Link as LinkIcon, OpenInNew, QuestionMark } from "@mui/icons-material"
import NewFeature from "modules/new-feature"
import Tooltip from "components/material/tooltip"
import HelpItem from "./components/help-item"
import { useTrack } from "helpers/analytics"

import "./styles.scss"

export default function HelpMenu({ id, bundle, saleSheet, handbookUrl, helpArticles }) {
  const [anchorEl, setAnchorEl] = useState(null)

  const itemTracking = useTrack({
    type: "help-menu-link",
    payload: ({ type, title, url }) => ({ type, title, url }),
  })

  const toggleTracking = useTrack({
    type: "help-menu-toggle",
    payload: (state) => ({ state }),
  })

  const handleToggle = useCallback(
    (openOrClose) => (e) => {
      toggleTracking(openOrClose)
      setAnchorEl(openOrClose === "open" ? e.currentTarget : null)
    }, [toggleTracking])

  const makeItemClick = (item) => () => {
    itemTracking(item)
    setAnchorEl(null)
  }

  const items = useMemo(() => {
    const { bundleId, bundleSaleSheet } = bundle ?? {}
    const generic = {
      type: "generic",
      url: "https://support.ompnt.com/",
      title: "Help center & support articles",
      icon: <LinkIcon fontSize="medium" />,
    }

    const specific = map(helpArticles, ({ url, title }) => ({
      type: "specific",
      url,
      title: `${title} support article`,
      icon: <LinkIcon fontSize="medium" />,
    }))

    const dataset = (saleSheet || bundleSaleSheet) && {
      type: "salesheet",
      url: saleSheet || bundleSaleSheet,
      title: `${id || bundleId} fact sheet`,
      icon: <OpenInNew fontSize="small" />,
    }

    const handbookUrlItem = handbookUrl && {
      type: "handbookUrl",
      url: handbookUrl,
      title: `${id || bundleId} handbook`,
      icon: <OpenInNew fontSize="small" />,
    }

    return compact([
      ...specific,
      dataset,
      handbookUrlItem,
      generic,
    ])
  }, [bundle, id, saleSheet, handbookUrl, helpArticles])

  const button = useMemo(() => <IconButton
    aria-haspopup="true"
    onClick={handleToggle("open")}
    className="help round"
  >
    <QuestionMark />
  </IconButton>, [handleToggle])

  // Add a divider for the generic help link only if there are multiple items
  const needDivider = (i) => size(items) > 1 && i === size(items) - 1

  return (
    <div className="modules_help-menu">
      <NewFeature id="help-menu" className="icon-button">
        <Tooltip title="Help menu">
          {button}
        </Tooltip>
      </NewFeature>
      <Popover
        id="help-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleToggle("close")}
        classes={{
          root: "help-menu-root",
          paper: "help-menu-paper",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {map(items, ({ url, title, type, icon }, i) => <Fragment key={`item-${i}`}>
          {needDivider(i) && <Divider />}
          <HelpItem key={url} url={url} title={title} icon={icon} onClick={makeItemClick({ url, type, title })} />
        </Fragment>,
        )}
      </Popover>
    </div>
  )
}

HelpMenu.propTypes = {
  modelId: PropTypes.string,
  id: PropTypes.string,
  saleSheet: PropTypes.string,
  handbookUrl: PropTypes.string,
  bundle: PropTypes.shape({
    bundleId: PropTypes.string,
    bundleSaleSheet: PropTypes.string,
  }),
  helpArticles: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  })),
}
